/* Toont een switch per eigen kind waarmee de ouder `toestemming_foto`
 * en `toestemming_uitstapje` zelf kan wijzigen. Gebruikt de dedicated
 * ouder-endpoint /api/portaal/kind/{kid}/consent. */
import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Camera, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function ConsentControls() {
  const [kids, setKids] = useState([]);
  const [busy, setBusy] = useState({}); // {kid_id: bool}

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portaal/overview");
        setKids(data.kinderen || []);
      } catch (e) { /* silent — kaart valt terug op leeg */ }
    })();
  }, []);

  const toggle = async (kid, field, value) => {
    setBusy(b => ({ ...b, [kid.id + field]: true }));
    // Optimistic
    setKids(cur => cur.map(k => k.id === kid.id ? { ...k, [field]: value } : k));
    try {
      await api.patch(`/portaal/kind/${kid.id}/consent`, { [field]: value });
      toast.success(`${field === "toestemming_foto" ? "Foto's" : "Uitstapjes"}: ${value ? "aan" : "uit"} voor ${kid.voornaam}`);
    } catch (e) {
      // Revert
      setKids(cur => cur.map(k => k.id === kid.id ? { ...k, [field]: !value } : k));
      toast.error(formatApiError(e.response?.data?.detail) || "Bijwerken mislukt");
    } finally {
      setBusy(b => ({ ...b, [kid.id + field]: false }));
    }
  };

  if (!kids.length) {
    return (
      <div className="p-4 text-sm text-neutral-500" data-testid="consent-empty">
        Geen kinderen gekoppeld aan dit account.
      </div>
    );
  }

  return (
    <div className="divide-y divide-neutral-100" data-testid="consent-controls">
      {kids.map(k => (
        <div key={k.id} className="p-4">
          <div className="text-sm font-medium text-neutral-800 mb-3">{k.voornaam} {k.achternaam}</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[color:var(--terracotta)]/10 text-[color:var(--terracotta)] flex items-center justify-center shrink-0">
                <Camera className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-800">Foto's toestaan</div>
                <div className="text-[11px] text-neutral-500">Leidsters mogen foto's maken van {k.voornaam} voor het schriftje.</div>
              </div>
              <Switch
                checked={!!k.toestemming_foto}
                onCheckedChange={(v) => toggle(k, "toestemming_foto", v)}
                disabled={busy[k.id + "toestemming_foto"]}
                data-testid={`consent-foto-${k.id}`}
              />
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[color:var(--sage-100)] text-[color:var(--sage)] flex items-center justify-center shrink-0">
                <MapPin className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-800">Uitstapjes toestaan</div>
                <div className="text-[11px] text-neutral-500">{k.voornaam} mag mee op locatie-uitjes.</div>
              </div>
              <Switch
                checked={!!k.toestemming_uitstapje}
                onCheckedChange={(v) => toggle(k, "toestemming_uitstapje", v)}
                disabled={busy[k.id + "toestemming_uitstapje"]}
                data-testid={`consent-uitstapje-${k.id}`}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
